import { Trans, useTranslation } from 'react-i18next'
import { Base, ReverLoading, toast } from '@itsrever/design-system'
import { useAppSelector } from '@/redux/hooks'
import { seesEveryGroup } from '@/domain/store-group'
import { useWithdrawalSettings } from '@/hooks/use-withdrawal-settings'
import NotFound from '@/ui/pages/404.page'
import { NavigationTabs, PageHeader } from '@/ui/shared'
import {
  SettingsPanel,
  SwitchRow
} from '@/ui/components/group-settings/form-parts'
import { useCustomisationTabs } from './configuration-tabs'

const { Button, Callout } = Base

export default function WithdrawalSettingsPage() {
  const { t } = useTranslation()
  const tabs = useCustomisationTabs()
  const profile = useAppSelector((s) => s.userApi.getMe)
  const ecommerceId = useAppSelector((s) => s.generalData.selectedEcommerce)
  const { enabled, loading, failed, reload, saving, setEnabled } =
    useWithdrawalSettings(ecommerceId)

  // getMe is dispatched from an effect: deciding before it lands would flash the 404 at REVER.
  if (profile.loading === 'idle' || profile.loading === 'pending')
    return <ReverLoading />
  if (!seesEveryGroup(profile.response.user)) return <NotFound />

  const save = (on: boolean) =>
    setEnabled(on).catch(() =>
      toast({ text: t('withdrawal_settings.save_error'), variant: 'error' })
    )

  return (
    <>
      <NavigationTabs
        tabs={tabs}
        selectedIndex={tabs.findIndex(
          (tab) => tab.link === '/configuration/withdrawal'
        )}
      />
      <PageHeader
        title={t('withdrawal_settings.title')}
        description={t('withdrawal_settings.description')}
      />
      <div className="flex max-w-3xl flex-col gap-4 px-7 py-4">
        <Callout tone="info" className="text-xs leading-relaxed">
          <Trans
            i18nKey="withdrawal_settings.rever_only_banner"
            components={{ b: <b /> }}
          />
        </Callout>
        {loading ? (
          <ReverLoading />
        ) : failed ? (
          <div className="flex items-center gap-3 text-sm text-neutral-600">
            {t('withdrawal_settings.load_error')}
            <Button variant="secondary" size="sm" onClick={() => reload()}>
              {t('load_error.retry')}
            </Button>
          </div>
        ) : (
          <SettingsPanel>
            <SwitchRow
              label={t('withdrawal_settings.enabled_label')}
              description={t('withdrawal_settings.enabled_description')}
              checked={enabled}
              onChange={save}
              readOnly={saving}
            />
          </SettingsPanel>
        )}
      </div>
    </>
  )
}
